import type { Cursor, Id } from '../../../../api/types';
import type { setupPlugins } from './plugins';
import { createVisitorCursorManager } from './visitor-cursors';

type Area = ReturnType<typeof setupPlugins>['area'];

type CursorSyncOptions = {
  area: Area;
  container: HTMLElement;
  getWorkflowId: () => Id | undefined;
  getVisitorId: () => string | undefined;
  updateRemoteCursor: (
    workflowId: Id,
    visitorId: string,
    cursor: Cursor
  ) => Promise<unknown>;
};

export function createCursorSync({
  area,
  container,
  getWorkflowId,
  getVisitorId,
  updateRemoteCursor,
}: CursorSyncOptions) {
  const {
    updateVisitorCursor,
    removeVisitorCursor,
    clearVisitorCursors,
    updateCursorScale,
  } = createVisitorCursorManager(area);

  let cursorIntervalMs = 5000;
  let lastCursorSent = 0;

  const sendCursor = (cursor: Cursor) => {
    const workflowId = getWorkflowId();
    const visitorId = getVisitorId();
    if (!workflowId || !visitorId) {
      return;
    }
    const now = Date.now();
    if (now - lastCursorSent < cursorIntervalMs) return;
    lastCursorSent = now;
    updateRemoteCursor(workflowId, visitorId, cursor).catch(console.error);
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!getWorkflowId() || !getVisitorId()) {
      return;
    }
    area.area.setPointerFrom(e);
    const { x, y } = area.area.pointer;
    sendCursor({ x, y });
  };

  container.addEventListener('pointermove', onPointerMove);

  const handlePipe = (context: any) => {
    if (context.type === 'zoomed') {
      updateCursorScale();
    }
    return context;
  };

  return {
    handlePipe,
    updateVisitorCursor: (id: string, color: string, cursor: Cursor) => {
      if (getVisitorId() === id) return;
      updateVisitorCursor(id, color, cursor);
    },
    removeVisitorCursor,
    clearVisitorCursors,
    setCursorUpdateInterval: (ms: number) => {
      cursorIntervalMs = ms;
    },
    destroy: () => {
      container.removeEventListener('pointermove', onPointerMove);
      clearVisitorCursors();
    },
  };
}
